import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    AppBar,
    Box,
    Drawer,
    List,
    Toolbar,
    Typography,
    IconButton,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Paper,
    Chip,
    Backdrop,
    CircularProgress,
    Collapse,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Tooltip,
    Divider,
    InputLabel,
    ListSubheader,
} from '@mui/material';
import { Dashboard as DashboardIcon, Logout, Lock, LockOpen, ExpandMore, Search, Warning as WarningIcon, Business as BusinessIcon, Menu as MenuIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';
import apiService from '../../services/api.service';
import { showSnackbar } from '../../store/slices/uiSlice';
import { AppDispatch, RootState } from '../../store';
import { logout, getProfile } from '../../store/slices/authSlice';
import { setSidebarOpen } from '../../store/slices/uiSlice';
import MODULES, { MENU_GROUPS } from '../../config/moduleRegistry';
import SearchResults from '../common/SearchResults';
import { useIsMobile } from '../../hooks/useBreakpoints';
import ResponsiveDialog from '../common/ResponsiveDialog';
import MySapAccountDialog from '../sapApproval/MySapAccountDialog';

const DRAWER_WIDTH = 240;
const WINDOW_TABS_STORAGE_KEY = 'app_window_tabs_v2';
const ACTIVE_WINDOW_STORAGE_KEY = 'app_active_window_v2';

type WindowTab = { id: number; key: string; title: string; minimized: boolean };

interface MainLayoutProps {
    children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: RootState) => state.auth);
    const { sidebarOpen } = useSelector((state: RootState) => state.ui);
    const isMobile = useIsMobile();
    const autoHideSidebar = !isMobile;
    const [windows, setWindows] = React.useState<WindowTab[]>(() => {
        try {
            const parsed = JSON.parse(sessionStorage.getItem(WINDOW_TABS_STORAGE_KEY) || '[]');
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    });
    const [activeWindowId, setActiveWindowId] = React.useState<number | null>(() => {
        try {
            const raw = sessionStorage.getItem(ACTIVE_WINDOW_STORAGE_KEY);
            return raw ? Number(raw) : null;
        } catch {
            return null;
        }
    });
    const [companies, setCompanies] = React.useState<{ CompanyID: number; CompanyCode?: string; CompanyName: string; DatabaseName?: string }[]>([]);
    const [selectedCompanyId, setSelectedCompanyId] = React.useState<number | ''>(() => {
        try { return Number(localStorage.getItem('selectedCompanyId') || '') as any; } catch { return ''; }
    });
    const [companyLocked, setCompanyLocked] = React.useState(true);
    const [pendingCompanyId, setPendingCompanyId] = React.useState<string | null>(null);
    const [showCompanyChangeConfirm, setShowCompanyChangeConfirm] = React.useState(false);
    const [globalSearchOpen, setGlobalSearchOpen] = React.useState(false);
    const [globalSearchQuery, setGlobalSearchQuery] = React.useState('');
    const [showSearchResults, setShowSearchResults] = React.useState(false);
    // Mobile-only: bottom sheet for company picker + secondary controls.
    const [mobileMoreOpen, setMobileMoreOpen] = React.useState(false);
    const [sapAccountOpen, setSapAccountOpen] = React.useState(false);
    const [mobileSearchOpen, setMobileSearchOpen] = React.useState(false);

    // Auto-close the drawer when crossing into mobile so the persistent layout
    // doesn't leave it open behind a temporary overlay.
    const wasMobileRef = React.useRef(isMobile);
    React.useEffect(() => {
        if (isMobile && !wasMobileRef.current) {
            dispatch(setSidebarOpen(false));
        }
        if (!isMobile && wasMobileRef.current) {
            dispatch(setSidebarOpen(true));
        }
        wasMobileRef.current = isMobile;
    }, [isMobile, dispatch]);

    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                setGlobalSearchOpen(true);
                const searchInput = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement;
                if (searchInput) searchInput.focus();
            }
            if (e.key === 'Escape') {
                if (globalSearchOpen) {
                    setGlobalSearchOpen(false);
                    setShowSearchResults(false);
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [globalSearchOpen]);

    React.useEffect(() => {
        if (globalSearchQuery.trim().length >= 2) {
            setShowSearchResults(true);
        } else {
            setShowSearchResults(false);
        }
    }, [globalSearchQuery]);

    const loadCompanies = React.useCallback(async () => {
        try {
            let list: Array<{ CompanyID: number; CompanyCode?: string; CompanyName: string; DatabaseName?: string }> = [];
            try {
                const resUser = await apiService.getMyCompanies(true);
                const rawUser = Array.isArray(resUser) ? resUser : (resUser as any)?.data || [];
                list = rawUser.map((c: any) => ({ CompanyID: c.CompanyID ?? c.companyid, CompanyCode: c.CompanyCode ?? c.companycode, CompanyName: c.CompanyName ?? c.companyname, DatabaseName: c.DatabaseName ?? c.databasename }));
            } catch {}
            if (!list.length) {
                try {
                    const resAll = await apiService.getCompanies(true);
                    const rawAll = Array.isArray(resAll) ? resAll : (resAll as any)?.data || [];
                    list = rawAll.map((c: any) => ({ CompanyID: c.CompanyID ?? c.companyid, CompanyCode: c.CompanyCode ?? c.companycode, CompanyName: c.CompanyName ?? c.companyname, DatabaseName: c.DatabaseName ?? c.databasename }));
                } catch {}
            }
            setCompanies(list);
            try {
                const ids = new Set(list.map((c) => c.CompanyID));
                const saved = Number(localStorage.getItem('selectedCompanyId') || '');
                const effective = (saved || selectedCompanyId) as any;
                if (effective && !ids.has(Number(effective))) {
                    setSelectedCompanyId('');
                    try { localStorage.removeItem('selectedCompanyId'); } catch {}
                }
            } catch {}
        } catch (e: any) {
            dispatch(showSnackbar({ message: e?.response?.data?.message || 'Failed to load companies', severity: 'error' }));
        }
    }, [dispatch, selectedCompanyId]);

    React.useEffect(() => { void loadCompanies(); }, [loadCompanies]);

    const handleLogout = () => {
        commitWindows([]);
        commitActiveWindowId(null);
        dispatch(logout());
        navigate('/login');
    };

    const handleCompanyChange = (companyId: string) => {
        if (companyId === String(selectedCompanyId)) return;

        if (companyLocked) {
            setPendingCompanyId(companyId);
            setShowCompanyChangeConfirm(true);
        } else {
            confirmCompanyChange(companyId);
        }
    };

    const confirmCompanyChange = (companyId: string) => {
        commitWindows([]);
        commitActiveWindowId(null);
        setClosingOverlay(false);
        setSelectedCompanyId(Number(companyId));
        localStorage.setItem('selectedCompanyId', companyId);
        window.dispatchEvent(new CustomEvent('company-changed', { detail: { companyId } }));
        setCompanyLocked(true);
        setShowCompanyChangeConfirm(false);
        setPendingCompanyId(null);
        navigate('/dashboard');
    };

    const cancelCompanyChange = () => {
        setShowCompanyChangeConfirm(false);
        setPendingCompanyId(null);
    };

    const companySelected = !!selectedCompanyId;
    const forceChild = location.pathname.startsWith('/pr/') || location.pathname.startsWith('/po/');
    const [closingOverlay, setClosingOverlay] = React.useState(false);
    const getModuleByKey = React.useCallback((key: string) => MODULES.find((m) => m.key === key), []);
    const persistWindows = React.useCallback((nextWindows: WindowTab[]) => {
        try { sessionStorage.setItem(WINDOW_TABS_STORAGE_KEY, JSON.stringify(nextWindows)); } catch {}
    }, []);
    const persistActiveWindowId = React.useCallback((nextId: number | null) => {
        try {
            if (nextId === null) sessionStorage.removeItem(ACTIVE_WINDOW_STORAGE_KEY);
            else sessionStorage.setItem(ACTIVE_WINDOW_STORAGE_KEY, String(nextId));
        } catch {}
    }, []);
    const commitWindows = React.useCallback((nextWindows: WindowTab[]) => {
        setWindows(nextWindows);
        persistWindows(nextWindows);
    }, [persistWindows]);
    const commitActiveWindowId = React.useCallback((nextId: number | null) => {
        setActiveWindowId(nextId);
        persistActiveWindowId(nextId);
    }, [persistActiveWindowId]);
    const getTabModuleForPath = React.useCallback((path: string) => {
        if (path === '/payment-requests' || path.startsWith('/payment-requests/')) {
            return MODULES.find((m) => m.key === 'payment-requests');
        }
        return MODULES.find((m) => m.path === path);
    }, []);

    const openWindow = (key: string, title: string, path?: string) => {
        if (!companySelected) {
            dispatch(showSnackbar({ message: 'Please select a company to continue', severity: 'warning' }));
            return;
        }
        const existing = windows.find((w) => w.key === key);
        if (existing) {
            const nextWindows = windows.map((w) => (w.id === existing.id ? { ...w, minimized: false } : w));
            commitWindows(nextWindows);
            commitActiveWindowId(existing.id);
        } else {
            const id = Date.now();
            const nextWindows = [...windows, { id, key, title, minimized: false }];
            commitWindows(nextWindows);
            commitActiveWindowId(id);
        }
        if (path && forceChild) {
            setClosingOverlay(true);
            setTimeout(() => { navigate(path); }, 150);
        } else if (path) {
            navigate(path);
        }
        if (autoHideSidebar) {
            dispatch(setSidebarOpen(false));
        }
    };

    const handleDashboardClick = (event: React.MouseEvent, path?: string) => {
        event.preventDefault();
        event.stopPropagation();
        if (!companySelected) {
            dispatch(showSnackbar({ message: 'Please select a company to continue', severity: 'warning' }));
            return;
        }
        if (!path) return;
        commitActiveWindowId(null);
        setClosingOverlay(false);
        navigate(path);
        if (autoHideSidebar) {
            dispatch(setSidebarOpen(false));
        }
    };

    const handleDashboardShortcut = () => {
        dispatch(setSidebarOpen(true));
        commitActiveWindowId(null);
        setClosingOverlay(false);
        if (location.pathname !== '/dashboard') {
            navigate('/dashboard');
        }
    };

    const closeWindow = (id: number) => {
        const closingWindow = windows.find((w) => w.id === id);
        const remaining = windows.filter((w) => w.id !== id);
        commitWindows(remaining);
        if (activeWindowId === id) {
            commitActiveWindowId(null);
        }
        if (!closingWindow) return;
        const closingPath = getModuleByKey(closingWindow.key)?.path;
        const activeRouteModuleKey = getTabModuleForPath(location.pathname)?.key;
        if (closingPath && activeRouteModuleKey === closingWindow.key) {
            const nextWindow = remaining.find((w) => !w.minimized) ?? remaining[0];
            const nextPath = nextWindow ? getModuleByKey(nextWindow.key)?.path : '/dashboard';
            commitActiveWindowId(nextWindow?.id ?? null);
            navigate(nextPath || '/dashboard');
        }
    };

    const minimizeWindow = (id: number) => {
        const nextWindows = windows.map((w) => (w.id === id ? { ...w, minimized: true } : w));
        commitWindows(nextWindows);
        if (activeWindowId === id) {
            commitActiveWindowId(null);
            const nextWindow = nextWindows.find((w) => w.id !== id && !w.minimized);
            const nextPath = nextWindow ? getModuleByKey(nextWindow.key)?.path : '/dashboard';
            commitActiveWindowId(nextWindow?.id ?? null);
            navigate(nextPath || '/dashboard');
        }
    };

    const restoreWindow = (id: number) => {
        const nextWindows = windows.map((w) => (w.id === id ? { ...w, minimized: false } : w));
        commitWindows(nextWindows);
        commitActiveWindowId(id);
        const path = getModuleByKey(windows.find((w) => w.id === id)?.key || '')?.path;
        if (path) navigate(path);
    };

    const activateWindow = (id: number) => {
        const target = windows.find((w) => w.id === id);
        if (!target) return;
        commitActiveWindowId(id);
        const path = getModuleByKey(target.key)?.path;
        if (path && location.pathname !== path) navigate(path);
    };

    React.useEffect(() => {
        try {
            localStorage.removeItem('app_windows_v1');
            localStorage.removeItem('app_active_window_v1');
        } catch {}
    }, []);
    React.useEffect(() => {
        if (!location.pathname.startsWith('/pr/') && !location.pathname.startsWith('/po/')) {
            setClosingOverlay(false);
        }
    }, [location.pathname]);

    React.useEffect(() => {
        try {
            window.scrollTo({ left: 0 });
            document.documentElement.scrollLeft = 0;
            document.body.scrollLeft = 0;
        } catch {}
    }, [location.pathname, sidebarOpen]);

    // Refresh user profile (including allowed modules) when company changes
    React.useEffect(() => {
        const handleCompanyChange = (e: any) => {
            const newCompanyId = e.detail?.companyId;
            if (newCompanyId) {
                setSelectedCompanyId(Number(newCompanyId));
            }
            dispatch(getProfile() as any);
        };
        window.addEventListener('company-changed', handleCompanyChange);
        return () => window.removeEventListener('company-changed', handleCompanyChange);
    }, [dispatch]);

    const allowedModules: string[] = ((user as any)?.allowedmodules ?? []) as string[];
    const selectedCompanyName = React.useMemo(() => {
        const id = Number(selectedCompanyId || 0);
        if (!id) return '';
        const found = companies.find((c) => Number(c.CompanyID) === id);
        return found ? String(found.CompanyName || '') : '';
    }, [companies, selectedCompanyId]);
    const selectedCompanyCode = React.useMemo(() => {
        const id = Number(selectedCompanyId || 0);
        if (!id) return '';
        const found = companies.find((c) => Number(c.CompanyID) === id);
        return found ? String(found.CompanyCode || '') : '';
    }, [companies, selectedCompanyId]);
    const selectedDatabaseName = React.useMemo(() => {
        const id = Number(selectedCompanyId || 0);
        if (!id) return '';
        const found = companies.find((c) => Number(c.CompanyID) === id);
        return found ? String(found.DatabaseName || '') : '';
    }, [companies, selectedCompanyId]);

    // Compute menu strictly from assigned modules
    const canSee = (item: typeof MODULES[number]) => {
        const moduleOk = !item.moduleKey ? true : allowedModules.includes(item.moduleKey);
        return moduleOk && !item.hideFromMenu;
    };

    // Build grouped menu: show groups with permitted children; also show single items not part of any group
    const groupedKeys = new Set<string>(MENU_GROUPS.flatMap((g) => g.children.map((c) => c.key)));
    const singleItems = MODULES
        .filter(canSee)
        .filter((item) => !groupedKeys.has(item.key))
        .sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
    const menuGroups = MENU_GROUPS
        .map((g) => ({
            ...g,
            children: g.children.filter(canSee).sort((a, b) => (a.order ?? 100) - (b.order ?? 100)),
        }))
        .filter((g) => g.children.length > 0)
        .sort((a, b) => (a.order ?? 100) - (b.order ?? 100));

    // Separate groups to control rendering order (Dashboard first, then Workflow, then Settings)
    const dashboardGroup = menuGroups.find((g) => g.key === 'group-dashboard');
    const workGroup = menuGroups.find((g) => g.key === 'group-work');
    const settingsGroup = menuGroups.find((g) => g.key === 'group-settings');
    const otherGroups = menuGroups.filter((g) => g.key !== 'group-dashboard' && g.key !== 'group-work' && g.key !== 'group-settings');

    // Default all groups to collapsed; expand only when user clicks
    const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>(
        MENU_GROUPS.reduce((acc, g) => {
            acc[g.key] = false;
            return acc;
        }, {} as Record<string, boolean>)
    );
    const toggleGroup = (key: string) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

    React.useEffect(() => {
        const ensureAdminCompanies = async () => {
            try {
                const isAdminRole = String(user?.role || '').toLowerCase() === 'admin';
                if (!user?.userid || !isAdminRole) return;
                try { await apiService.grantAllUserCompanies(user.userid); } catch {}
                await loadCompanies();
            } catch {}
        };
        ensureAdminCompanies();
    }, [user?.userid, user?.role, loadCompanies]);

    React.useEffect(() => {
        if (windows.length > 0) {
            const exists = windows.some((w) => w.id === activeWindowId);
            if (!exists) {
                const target = windows.find((w) => !w.minimized) ?? windows[0];
                if (target) commitActiveWindowId(target.id);
            }
        }
    }, [windows, activeWindowId, commitActiveWindowId]);

    React.useEffect(() => {
        const currentModule = getTabModuleForPath(location.pathname);
        if (!currentModule || currentModule.key === 'dashboard' || currentModule.key === 'pr-dashboard') {
            return;
        }
        const existing = windows.find((w) => w.key === currentModule.key);
        if (existing) {
            if (existing.minimized) {
                commitWindows(windows.map((w) => (w.id === existing.id ? { ...w, minimized: false } : w)));
            }
            if (activeWindowId !== existing.id) {
                commitActiveWindowId(existing.id);
            }
            return;
        }
        const id = Date.now();
        commitWindows([...windows, { id, key: currentModule.key, title: currentModule.label, minimized: false }]);
        commitActiveWindowId(id);
    }, [location.pathname, windows, activeWindowId, getTabModuleForPath, commitWindows, commitActiveWindowId]);

    const toggleDrawer = () => dispatch(setSidebarOpen(!sidebarOpen));

    return (
        <Box sx={{ display: 'flex', backgroundColor: (theme) => theme.palette.background.paper }}>
            <AppBar
                component="header"
                position="fixed"
                color="primary"
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.12)',
                    backgroundColor: '#0a6ed1',
                    color: '#fff',
                    height: 56,
                }}
            >
                <Toolbar
                    sx={{
                        minHeight: '56px !important',
                        height: 56,
                        px: { xs: 1, sm: 2 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: { xs: 0.5, md: 1 },
                    }}
                >
                    {/* Left Section: Menu + Company Info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 2 }, minWidth: 0 }}>
                        {isMobile && (
                            <IconButton
                                color="inherit"
                                edge="start"
                                onClick={toggleDrawer}
                                aria-label={sidebarOpen ? 'Close navigation menu' : 'Open navigation menu'}
                                aria-expanded={sidebarOpen}
                                aria-controls="primary-navigation"
                                sx={{ width: 40, height: 40 }}
                            >
                                <MenuIcon sx={{ fontSize: 22 }} />
                            </IconButton>
                        )}
                        {allowedModules.includes('dashboard') && (
                            <IconButton
                                color="inherit"
                                edge={isMobile ? false : 'start'}
                                onClick={handleDashboardShortcut}
                                aria-label="Go to dashboard"
                                sx={{
                                    padding: 8,
                                    width: 36,
                                    height: 36,
                                    borderRadius: 1,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.1)',
                                    }
                                }}
                            >
                                <DashboardIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        )}

                        {/* Company Selection - Compact (hidden on mobile; surfaced via "More" sheet) */}
                        <Box sx={{
                            display: { xs: 'none', md: 'flex' },
                            alignItems: 'center',
                            gap: 1.5,
                            px: 1.5,
                            py: 0.75,
                            backgroundColor: 'rgba(255,255,255,0.08)',
                            borderRadius: 1.5,
                            border: '1px solid rgba(255,255,255,0.15)',
                        }}>
                            <BusinessIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.9)' }} />

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        color: 'rgba(255,255,255,0.7)',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        fontFamily: '"Arial", "Helvetica", sans-serif',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {selectedCompanyName || 'Select Company'}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '11px',
                                        fontWeight: 400,
                                        color: 'rgba(255,255,255,0.8)',
                                        fontFamily: '"Consolas", "Monaco", monospace',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {selectedDatabaseName || '—'}
                                </Typography>
                            </Box>

                            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: 0.5 }} />

                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel
                                    id="app-company-select-label"
                                    sx={{
                                        color: 'rgba(255,255,255,0.7)',
                                        fontSize: '12px',
                                        fontFamily: '"Arial", "Helvetica", sans-serif',
                                        fontWeight: 500,
                                        '&.Mui-focused': {
                                            color: 'rgba(255,255,255,0.9)',
                                        },
                                    }}
                                >
                                    Change
                                </InputLabel>
                                <Select
                                    labelId="app-company-select-label"
                                    id="app-company-select"
                                    value={selectedCompanyId}
                                    label="Change"
                                    onChange={(e) => handleCompanyChange(e.target.value as string)}
                                    disabled={companyLocked}
                                    renderValue={() => (selectedCompanyCode ? `${selectedCompanyCode} · ${selectedCompanyName}` : selectedCompanyName)}
                                    MenuProps={{ PaperProps: { sx: { minWidth: 640, maxWidth: 'calc(100vw - 32px)', maxHeight: '70vh' } } }}
                                    sx={{
                                        color: '#fff',
                                        fontSize: '12px',
                                        fontFamily: '"Arial", "Helvetica", sans-serif',
                                        backgroundColor: 'rgba(255,255,255,0.08)',
                                        '& .MuiSelect-select': {
                                            backgroundColor: 'transparent',
                                            py: 0.75,
                                        },
                                        '&.Mui-disabled': {
                                            backgroundColor: 'rgba(255,255,255,0.05)',
                                            color: 'rgba(255,255,255,0.5)',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(255,255,255,0.3)',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(255,255,255,0.5)',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(255,255,255,0.7)',
                                        },
                                    }}
                                >
                                    <ListSubheader
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '56px minmax(0, 1fr) 200px',
                                            columnGap: 2,
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            lineHeight: '32px',
                                            color: 'text.secondary',
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <span>Code</span>
                                        <span>Name</span>
                                        <span>Database</span>
                                    </ListSubheader>
                                    {companies.map((c) => (
                                        <MenuItem
                                            key={c.CompanyID}
                                            value={String(c.CompanyID)}
                                            sx={{
                                                fontSize: '12px',
                                                fontFamily: '"Arial", "Helvetica", sans-serif',
                                                py: 0.75,
                                            }}
                                        >
                                            <Box sx={{ display: 'grid', gridTemplateColumns: '56px minmax(0, 1fr) 200px', columnGap: 2, alignItems: 'center', width: '100%' }}>
                                                <Box sx={{ fontWeight: 600, color: '#0a6ed1', fontFamily: '"Consolas", "Monaco", monospace' }}>
                                                    {c.CompanyCode || '—'}
                                                </Box>
                                                <Box sx={{ fontWeight: 400, whiteSpace: 'normal' }}>
                                                    {c.CompanyName}
                                                </Box>
                                                <Box sx={{ color: 'text.secondary', fontFamily: '"Consolas", "Monaco", monospace', fontSize: '11px', wordBreak: 'break-all' }}>
                                                    {c.DatabaseName || '—'}
                                                </Box>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    {/* Mobile compact title — replaces the company chip + search field */}
                    {isMobile && (
                        <Box sx={{ flex: 1, minWidth: 0, mx: 1 }}>
                            <Typography
                                noWrap
                                sx={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: 'rgba(255,255,255,0.95)',
                                    fontFamily: '"Arial", "Helvetica", sans-serif',
                                }}
                            >
                                {selectedCompanyName || 'Select Company'}
                            </Typography>
                        </Box>
                    )}

                    {/* Center Section: Search (desktop) */}
                    <Box sx={{
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'center',
                        flex: 1,
                        maxWidth: 400,
                        mx: 2,
                    }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder={globalSearchOpen ? 'Search...' : 'Search... (Ctrl+K)'}
                            inputProps={{ 'aria-label': 'Global search' }}
                            value={globalSearchQuery}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGlobalSearchQuery(e.target.value)}
                            onFocus={() => setGlobalSearchOpen(true)}
                            onBlur={() => setTimeout(() => setGlobalSearchOpen(false), 200)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'rgba(255,255,255,0.12)',
                                    borderRadius: 2,
                                    height: 36,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '& fieldset': {
                                        borderColor: 'transparent',
                                        borderWidth: 1,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(255,255,255,0.3)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgba(255,255,255,0.5)',
                                        backgroundColor: 'rgba(255,255,255,0.18)',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    color: '#fff',
                                    fontSize: 13,
                                    fontFamily: '"Arial", "Helvetica", sans-serif',
                                    '&::placeholder': {
                                        color: 'rgba(255,255,255,0.6)',
                                        fontSize: 13,
                                        opacity: 0.7,
                                    },
                                },
                            }}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, fontSize: 18, color: 'rgba(255,255,255,0.7)' }} />,
                            }}
                        />
                    </Box>

                    {/* Right Section: Lock + User + Logout (desktop) */}
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5 }}>
                        {/* Lock/Unlock Button */}
                        <Tooltip
                            title={companyLocked ? "Company locked - Click to unlock" : "Company unlocked - Click to lock"}
                            arrow
                            placement="bottom"
                        >
                            <Button
                                size="small"
                                variant={companyLocked ? "contained" : "outlined"}
                                startIcon={companyLocked ? <Lock sx={{ fontSize: 15 }} /> : <LockOpen sx={{ fontSize: 15 }} />}
                                onClick={() => setCompanyLocked((v) => !v)}
                                aria-pressed={!companyLocked}
                                aria-label={companyLocked ? 'Company is locked, click to unlock' : 'Company is unlocked, click to lock'}
                                sx={{
                                    borderColor: companyLocked ? 'transparent' : 'rgba(255,255,255,0.3)',
                                    color: '#fff',
                                    backgroundColor: companyLocked ? 'rgba(244, 67, 54, 0.9)' : 'rgba(255,255,255,0.05)',
                                    fontFamily: '"Arial", "Helvetica", sans-serif',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    minHeight: 32,
                                    py: 0.6,
                                    px: 1.25,
                                    borderRadius: 1.5,
                                    letterSpacing: '0.25px',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        borderColor: 'rgba(255,255,255,0.5)',
                                        backgroundColor: companyLocked ? 'rgba(211, 47, 47, 1)' : 'rgba(255,255,255,0.15)',
                                        transform: 'translateY(-1px)',
                                    },
                                    '&:active': {
                                        transform: 'translateY(0)',
                                    },
                                }}
                            >
                                {companyLocked ? 'Locked' : 'Unlock'}
                            </Button>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', height: 24 }} />

                        {/* User Info: opens "My SAP B1 account" */}
                        <Tooltip title="My SAP B1 account" arrow placement="bottom">
                            <Button
                                onClick={() => setSapAccountOpen(true)}
                                aria-label={`${user?.fullname || 'My account'}: my SAP B1 account`}
                                sx={{
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    fontFamily: '"Arial", "Helvetica", sans-serif',
                                    color: 'rgba(255,255,255,0.95)',
                                    whiteSpace: 'nowrap',
                                    textTransform: 'none',
                                    minWidth: 0,
                                    px: 1,
                                }}
                            >
                                {user?.fullname}
                            </Button>
                        </Tooltip>

                        {/* Logout Button */}
                        <Button
                            variant="outlined"
                            onClick={handleLogout}
                            startIcon={<Logout sx={{ fontSize: 14 }} />}
                            sx={{
                                borderColor: 'rgba(255,255,255,0.3)',
                                color: '#fff',
                                backgroundColor: 'transparent',
                                fontFamily: '"Arial", "Helvetica", sans-serif',
                                fontWeight: 600,
                                minHeight: 32,
                                py: 0.6,
                                px: 1.25,
                                fontSize: '12px',
                                textTransform: 'none',
                                borderRadius: 1.5,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: 'rgba(255,255,255,0.5)',
                                    backgroundColor: 'rgba(255,255,255,0.15)',
                                }
                            }}
                        >
                            Logout
                        </Button>
                    </Box>

                    {/* Right Section: compact icon controls (mobile) */}
                    <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 0.25 }}>
                        <IconButton
                            color="inherit"
                            onClick={() => setMobileSearchOpen(true)}
                            aria-label="Open search"
                            sx={{ width: 40, height: 40 }}
                        >
                            <Search sx={{ fontSize: 22 }} />
                        </IconButton>
                        <IconButton
                            color="inherit"
                            onClick={() => setMobileMoreOpen(true)}
                            aria-label="More actions"
                            aria-haspopup="dialog"
                            sx={{ width: 40, height: 40 }}
                        >
                            <MoreVertIcon sx={{ fontSize: 22 }} />
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>

            {autoHideSidebar && !sidebarOpen && (
                <Box
                    aria-hidden="true"
                    onMouseEnter={() => dispatch(setSidebarOpen(true))}
                    sx={{
                        position: 'fixed',
                        left: 0,
                        top: 56,
                        bottom: 0,
                        width: 14,
                        zIndex: (theme) => theme.zIndex.drawer + 2,
                        cursor: 'ew-resize',
                        background: 'linear-gradient(90deg, rgba(10, 110, 209, 0.14), rgba(10, 110, 209, 0))',
                        opacity: 0.55,
                        transition: 'opacity 0.2s ease',
                        '&:hover': {
                            opacity: 1,
                        },
                    }}
                />
            )}

            <Drawer
                id="primary-navigation"
                component="nav"
                aria-label="Primary navigation"
                variant={isMobile ? 'temporary' : 'persistent'}
                open={sidebarOpen}
                onClose={isMobile ? () => dispatch(setSidebarOpen(false)) : undefined}
                onMouseLeave={autoHideSidebar ? () => dispatch(setSidebarOpen(false)) : undefined}
                ModalProps={{ keepMounted: true }}
                sx={{
                    width: { xs: DRAWER_WIDTH, md: sidebarOpen ? DRAWER_WIDTH : 0 },
                    flexShrink: 0,
                    transition: (theme) => theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.shorter,
                    }),
                    '& .MuiDrawer-paper': {
                        width: DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(247, 247, 247, 0.98) 100%)',
                        backdropFilter: 'blur(20px)',
                        borderRight: '1px solid rgba(255, 255, 255, 0.6)',
                        boxShadow: '4px 0 24px rgba(0, 0, 0, 0.06)',
                    },
                }}
            >
                <Toolbar sx={{ minHeight: '48px !important', height: 48 }} />
                <Box sx={{ overflow: 'auto', px: 1, pb: 2 }}>
                    <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        {/* Dashboard pages directly at the top */}
                        {dashboardGroup?.children.map((child) => {
                            const selected = location.pathname === child.path;
                            return (
                                <Box
                                    key={child.key}
                                    onClick={(event) => handleDashboardClick(event, child.path)}
                                    sx={{
                                        py: 0.75,
                                        px: 1,
                                        borderRadius: 0.5,
                                        background: selected ? '#0a6ed1' : 'transparent',
                                        border: selected ? 'none' : '1px solid transparent',
                                        opacity: companySelected ? 1 : 0.3,
                                        transition: 'all 0.15s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        cursor: 'pointer',
                                        '&:hover': companySelected ? {
                                            background: selected ? '#085bb0' : 'rgba(0, 0, 0, 0.04)',
                                        } : {},
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 20,
                                            height: 20,
                                        }}
                                    >
                                        {React.cloneElement(child.icon as React.ReactElement, {
                                            sx: {
                                                fontSize: 16,
                                                color: selected ? '#fff' : '#0a6ed1',
                                            }
                                        })}
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            fontWeight: selected ? 600 : 400,
                                            color: selected ? '#fff' : '#333',
                                        }}
                                    >
                                        {child.label}
                                    </Typography>
                                </Box>
                            );
                        })}

                        {/* 2) Work Flow group */}
                        {workGroup && (() => {
                            const isOpen = !!openGroups[workGroup.key];
                            return (
                                <Box key={workGroup.key}>
                                    <Box
                                        onClick={() => toggleGroup(workGroup.key)}
                                        sx={{
                                            py: 0.75,
                                            px: 1,
                                            borderRadius: 0.5,
                                            background: isOpen
                                                ? 'rgba(10, 110, 209, 0.08)'
                                                : 'transparent',
                                            border: isOpen ? '1px solid rgba(10, 110, 209, 0.15)' : '1px solid transparent',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 24,
                                                height: 24,
                                                borderRadius: 0.25,
                                                background: isOpen ? '#0a6ed1' : 'transparent',
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            <ExpandMore
                                                sx={{
                                                    fontSize: 14,
                                                    color: isOpen ? '#fff' : '#666',
                                                    transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                                                    transition: 'transform 0.2s ease',
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: '#333',
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.5,
                                            }}
                                        >
                                            {workGroup.label}
                                        </Typography>
                                    </Box>
                                    <Collapse in={isOpen} timeout={200}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.125, mt: 0.25 }}>
                                            {workGroup.children.map((child) => {
                                                const selected = location.pathname === child.path;
                                                return (
                                                    <Box
                                                        key={child.key}
                                                        onClick={() => openWindow(child.key, child.label, child.path)}
                                                        sx={{
                                                            py: 0.5,
                                                            px: 1,
                                                            ml: 1.5,
                                                            borderRadius: 0.25,
                                                            background: selected ? '#0a6ed1' : 'transparent',
                                                            border: selected ? 'none' : '1px solid transparent',
                                                            opacity: companySelected ? 1 : 0.3,
                                                            transition: 'all 0.15s ease',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.75,
                                                            cursor: 'pointer',
                                                            '&:hover': companySelected ? {
                                                                background: selected ? '#085bb0' : 'rgba(0, 0, 0, 0.04)',
                                                            } : {},
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: 20,
                                                                height: 20,
                                                            }}
                                                        >
                                                            {React.cloneElement(child.icon as React.ReactElement, {
                                                                sx: {
                                                                    fontSize: 16,
                                                                    color: selected ? '#fff' : '#0a6ed1',
                                                                }
                                                            })}
                                                        </Box>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 12,
                                                                fontWeight: selected ? 600 : 400,
                                                                color: selected ? '#fff' : '#333',
                                                            }}
                                                        >
                                                            {child.label}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                        })()}

                        {/* 2) Single items in the middle */}
                        {singleItems.map((item) => {
                            const selected = location.pathname === item.path;
                            return (
                                <Box
                                    key={item.key}
                                    onClick={() => openWindow(item.key, item.label, item.path)}
                                    sx={{
                                        py: 0.5,
                                        px: 1,
                                        borderRadius: 0.25,
                                        background: selected ? '#0a6ed1' : 'transparent',
                                        border: selected ? 'none' : '1px solid transparent',
                                        opacity: companySelected ? 1 : 0.3,
                                        transition: 'all 0.15s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        cursor: 'pointer',
                                        '&:hover': companySelected ? {
                                            background: selected ? '#085bb0' : 'rgba(0, 0, 0, 0.04)',
                                        } : {},
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 20,
                                            height: 20,
                                        }}
                                    >
                                        {React.cloneElement(item.icon as React.ReactElement, {
                                            sx: {
                                                fontSize: 16,
                                                color: selected ? '#fff' : '#0a6ed1',
                                            }
                                        })}
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            fontWeight: selected ? 600 : 400,
                                            color: selected ? '#fff' : '#333',
                                        }}
                                    >
                                        {item.label}
                                    </Typography>
                                </Box>
                            );
                        })}

                        {/* 3) Any other groups next */}
                        {otherGroups.map((group) => {
                            const isOpen = !!openGroups[group.key];
                            return (
                                <Box key={group.key}>
                                    <Box
                                        onClick={() => toggleGroup(group.key)}
                                        sx={{
                                            py: 0.75,
                                            px: 1,
                                            borderRadius: 0.5,
                                            background: isOpen
                                                ? 'rgba(10, 110, 209, 0.08)'
                                                : 'transparent',
                                            border: isOpen ? '1px solid rgba(10, 110, 209, 0.15)' : '1px solid transparent',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 24,
                                                height: 24,
                                                borderRadius: 0.25,
                                                background: isOpen ? '#0a6ed1' : 'transparent',
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            <ExpandMore
                                                sx={{
                                                    fontSize: 14,
                                                    color: isOpen ? '#fff' : '#666',
                                                    transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                                                    transition: 'transform 0.2s ease',
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: '#333',
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.5,
                                            }}
                                        >
                                            {group.label}
                                        </Typography>
                                    </Box>
                                    <Collapse in={isOpen} timeout={200}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.125, mt: 0.25 }}>
                                            {group.children.map((child) => {
                                                const selected = location.pathname === child.path;
                                                return (
                                                    <Box
                                                        key={child.key}
                                                        onClick={() => openWindow(child.key, child.label, child.path)}
                                                        sx={{
                                                            py: 0.5,
                                                            px: 1,
                                                            ml: 1.5,
                                                            borderRadius: 0.25,
                                                            background: selected ? '#0a6ed1' : 'transparent',
                                                            border: selected ? 'none' : '1px solid transparent',
                                                            opacity: companySelected ? 1 : 0.3,
                                                            transition: 'all 0.15s ease',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.75,
                                                            cursor: 'pointer',
                                                            '&:hover': companySelected ? {
                                                                background: selected ? '#085bb0' : 'rgba(0, 0, 0, 0.04)',
                                                            } : {},
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: 20,
                                                                height: 20,
                                                            }}
                                                        >
                                                            {React.cloneElement(child.icon as React.ReactElement, {
                                                                sx: {
                                                                    fontSize: 16,
                                                                    color: selected ? '#fff' : '#0a6ed1',
                                                                }
                                                            })}
                                                        </Box>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 12,
                                                                fontWeight: selected ? 600 : 400,
                                                                color: selected ? '#fff' : '#333',
                                                            }}
                                                        >
                                                            {child.label}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                        })}

                        {/* 4) Settings group last */}
                        {settingsGroup && (() => {
                            const isOpen = !!openGroups[settingsGroup.key];
                            return (
                                <Box key={settingsGroup.key}>
                                    <Box
                                        onClick={() => toggleGroup(settingsGroup.key)}
                                        sx={{
                                            py: 0.75,
                                            px: 1,
                                            borderRadius: 0.5,
                                            background: isOpen
                                                ? 'rgba(10, 110, 209, 0.08)'
                                                : 'transparent',
                                            border: isOpen ? '1px solid rgba(10, 110, 209, 0.15)' : '1px solid transparent',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 24,
                                                height: 24,
                                                borderRadius: 0.25,
                                                background: isOpen ? '#0a6ed1' : 'transparent',
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            <ExpandMore
                                                sx={{
                                                    fontSize: 14,
                                                    color: isOpen ? '#fff' : '#666',
                                                    transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                                                    transition: 'transform 0.2s ease',
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: '#333',
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.5,
                                            }}
                                        >
                                            {settingsGroup.label}
                                        </Typography>
                                    </Box>
                                    <Collapse in={isOpen} timeout={200}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.125, mt: 0.25 }}>
                                            {settingsGroup.children.map((child) => {
                                                const selected = location.pathname === child.path;
                                                return (
                                                    <Box
                                                        key={child.key}
                                                        onClick={() => openWindow(child.key, child.label, child.path)}
                                                        sx={{
                                                            py: 0.5,
                                                            px: 1,
                                                            ml: 1.5,
                                                            borderRadius: 0.25,
                                                            background: selected ? '#0a6ed1' : 'transparent',
                                                            border: selected ? 'none' : '1px solid transparent',
                                                            opacity: companySelected ? 1 : 0.3,
                                                            transition: 'all 0.15s ease',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.75,
                                                            cursor: 'pointer',
                                                            '&:hover': companySelected ? {
                                                                background: selected ? '#085bb0' : 'rgba(0, 0, 0, 0.04)',
                                                            } : {},
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: 20,
                                                                height: 20,
                                                            }}
                                                        >
                                                            {React.cloneElement(child.icon as React.ReactElement, {
                                                                sx: {
                                                                    fontSize: 16,
                                                                    color: selected ? '#fff' : '#0a6ed1',
                                                                }
                                                            })}
                                                        </Box>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 12,
                                                                fontWeight: selected ? 600 : 400,
                                                                color: selected ? '#fff' : '#333',
                                                            }}
                                                        >
                                                            {child.label}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                        })()}
                    </List>
                </Box>
            </Drawer>

            <Box
                component="main"
                id="main-content"
                tabIndex={-1}
                sx={{
                    flexGrow: 1,
                    p: { xs: 1, sm: 2, md: 3 },
                    width: { xs: '100%', md: sidebarOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%' },
                    minWidth: 0,
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    overflowX: 'hidden',
                    marginLeft: 0,
                    transition: (theme) => theme.transitions.create(['width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.shorter,
                    }),
                }}
            >
                <Toolbar sx={{ minHeight: '48px !important', height: 48 }} />
                {windows.length > 0 && !forceChild && (
                    <Box
                        role="tablist"
                        aria-label="Open document tabs"
                        sx={{
                            mb: 2,
                            px: 0,
                            py: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: { xs: 'nowrap', md: 'wrap' },
                            overflowX: { xs: 'auto', md: 'visible' },
                            scrollSnapType: { xs: 'x proximity', md: 'none' },
                            // Hide the scrollbar on mobile but keep keyboard scroll working.
                            '&::-webkit-scrollbar': { display: { xs: 'none', md: 'auto' } as any },
                            scrollbarWidth: 'thin',
                        }}
                    >
                        {windows.filter((w) => !w.minimized).map((w) => {
                            const isActive = w.id === activeWindowId;
                            return (
                                <Box
                                    key={w.id}
                                    onClick={() => activateWindow(w.id)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.5,
                                        py: 0.6,
                                        borderRadius: 2,
                                        background: isActive
                                            ? 'linear-gradient(135deg, #0a6ed1 0%, #085bb0 100%)'
                                            : 'rgba(255, 255, 255, 0.7)',
                                        backdropFilter: isActive ? 'blur(20px)' : 'blur(10px)',
                                        border: isActive
                                            ? 'none'
                                            : '1px solid rgba(255, 255, 255, 0.8)',
                                        boxShadow: isActive
                                            ? '0 4px 16px rgba(10, 110, 209, 0.35), 0 2px 8px rgba(10, 110, 209, 0.2)'
                                            : '0 1px 4px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        position: 'relative',
                                        minHeight: 36,
                                        userSelect: 'none',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: isActive
                                                ? '0 6px 20px rgba(10, 110, 209, 0.4), 0 3px 12px rgba(10, 110, 209, 0.25)'
                                                : '0 3px 8px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
                                        },
                                        '&:active': {
                                            transform: 'translateY(-1px)',
                                        },
                                    }}
                                >
                                    <Typography 
                                        sx={{ 
                                            fontSize: 12,
                                            fontWeight: isActive ? 600 : 500,
                                            color: isActive ? '#fff' : '#2c3e50',
                                            letterSpacing: 0.1,
                                        }}
                                    >
                                        {w.title}
                                    </Typography>
                                    <Box 
                                        sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 0.25,
                                            ml: 0.25,
                                            opacity: 0,
                                            transition: 'opacity 0.2s ease',
                                        }}
                                        className="tab-actions"
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.opacity = '1';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.opacity = '0';
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                    >
                                        {w.minimized ? (
                                            <IconButton 
                                                component="span" 
                                                size="small" 
                                                onClick={(e) => { e.stopPropagation(); restoreWindow(w.id); }}
                                                sx={{ 
                                                    p: 0.25,
                                                    ml: 0.5,
                                                    color: isActive ? '#fff' : '#757575',
                                                    width: 20,
                                                    height: 20,
                                                    minWidth: 20,
                                                    '&:hover': {
                                                        backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
                                                        borderRadius: 1,
                                                    }
                                                }}
                                            >
                                                <Box sx={{ fontSize: 12, fontWeight: 600 }}>□</Box>
                                            </IconButton>
                                        ) : (
                                            <IconButton 
                                                component="span" 
                                                size="small" 
                                                onClick={(e) => { e.stopPropagation(); minimizeWindow(w.id); }}
                                                sx={{ 
                                                    p: 0.25,
                                                    color: isActive ? '#fff' : '#757575',
                                                    width: 20,
                                                    height: 20,
                                                    minWidth: 20,
                                                    '&:hover': {
                                                        backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
                                                        borderRadius: 1,
                                                    }
                                                }}
                                            >
                                                <Box sx={{ fontSize: 14, fontWeight: 300 }}>–</Box>
                                            </IconButton>
                                        )}
                                        <IconButton 
                                            component="span" 
                                            size="small" 
                                            onClick={(e) => { e.stopPropagation(); closeWindow(w.id); }}
                                            sx={{ 
                                                p: 0.25,
                                                color: isActive ? '#fff' : '#757575',
                                                width: 20,
                                                height: 20,
                                                minWidth: 20,
                                                '&:hover': {
                                                    backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
                                                    borderRadius: 1,
                                                }
                                            }}
                                        >
                                            <Box sx={{ fontSize: 16, fontWeight: 300 }}>×</Box>
                                        </IconButton>
                                    </Box>
                                    <style>{`
                                        [class*="tab-actions"]:hover {
                                            opacity: 1 !important;
                                        }
                                    `}</style>
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {companySelected ? (
                    <Box sx={{ position: forceChild ? 'fixed' : 'relative', inset: 0, zIndex: forceChild ? (theme) => theme.zIndex.modal + 1 : 'auto', display: 'flex', alignItems: 'stretch', justifyContent: 'center', backgroundColor: forceChild ? 'rgba(0,0,0,0.08)' : 'transparent', overflowY: 'auto' }} aria-modal={forceChild ? true : undefined} role={forceChild ? 'dialog' : undefined}>
                        <Box sx={{ flex: 1, minWidth: 0, width: '100%', maxWidth: '100%', overflowX: 'hidden', p: 0 }}>{children}</Box>
                        <Backdrop open={closingOverlay} sx={{ zIndex: (theme) => theme.zIndex.modal + 2, color: '#fff' }}>
                            <CircularProgress color="inherit" />
                        </Backdrop>
                    </Box>
                ) : (
                    <Paper 
                        sx={{ 
                            p: 3, 
                            border: '1px dashed #cfd8dc',
                            borderRadius: 1.5,
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
                        }}
                    >
                        <Typography variant="h6" gutterBottom>Select a company to continue</Typography>
                        <Typography variant="body2">Use the Company dropdown in the top bar. Your selection will persist across refreshes.</Typography>
                    </Paper>
                )}
                

                {!forceChild && windows.some((w) => w.minimized) && (
                    <Box sx={{
                        position: 'fixed',
                        bottom: 0,
                        // On mobile the drawer is temporary (overlay) so the
                        // dock spans the full width.
                        left: { xs: 0, md: sidebarOpen ? DRAWER_WIDTH : 0 },
                        right: 0,
                        p: 1.5,
                        borderTop: '1px solid rgba(255, 255, 255, 0.8)',
                        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(247, 247, 247, 0.98) 100%)',
                        backdropFilter: 'blur(20px)',
                        display: 'flex',
                        gap: 0.75,
                        flexWrap: 'wrap',
                        zIndex: 1000,
                        transition: 'left 0.3s',
                        boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08), 0 -2px 8px rgba(0, 0, 0, 0.04)',
                    }}>
                        {windows.filter((w) => w.minimized).map((w) => (
                            <Chip
                                key={w.id}
                                label={w.title}
                                onClick={() => restoreWindow(w.id)}
                                onDelete={() => closeWindow(w.id)}
                                variant="outlined"
                                sx={{
                                    borderRadius: 1.25,
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(10, 110, 209, 0.2)',
                                    color: '#2c3e50',
                                    fontWeight: 500,
                                    fontSize: 12,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(10, 110, 209, 0.08)',
                                        borderColor: 'rgba(10, 110, 209, 0.3)',
                                        boxShadow: '0 2px 8px rgba(10, 110, 209, 0.15)',
                                        transform: 'translateY(-2px)',
                                    },
                                    '& .MuiChip-deleteIcon': {
                                        color: '#757575',
                                        fontSize: 16,
                                        '&:hover': {
                                            color: '#d32f2f',
                                        }
                                    }
                                }}
                            />
                         ))}
                     </Box>
                 )}
             </Box>

             <Dialog
                open={showCompanyChangeConfirm}
                onClose={cancelCompanyChange}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        border: '2px solid #f57c00',
                        boxShadow: '0 8px 32px rgba(245, 124, 0, 0.25)',
                    }
                }}
             >
                <DialogTitle sx={{
                    bgcolor: '#fff3e0',
                    color: '#e65100',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    borderBottom: '2px solid #f57c00',
                    py: 2,
                    px: 2.5,
                }}>
                    <WarningIcon sx={{ fontSize: 32, color: '#f57c00' }} />
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: '"Arial", "Helvetica", sans-serif', fontSize: '18px' }}>
                            Confirm Company Change
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: '"Arial", "Helvetica", sans-serif', fontSize: '12px', color: '#e65100', opacity: 0.8 }}>
                            This action will reload your permissions
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 2, px: 2.5 }}>
                    <Alert severity="warning" sx={{ mb: 3, borderRadius: 1, fontFamily: '"Arial", "Helvetica", sans-serif' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: '"Arial", "Helvetica", sans-serif' }}>
                            The company selection is currently locked for your safety
                        </Typography>
                    </Alert>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: '#616161',
                                    mb: 1,
                                    fontFamily: '"Arial", "Helvetica", sans-serif',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                Current Company
                            </Typography>
                            <Box sx={{
                                bgcolor: '#f5f5f5',
                                p: 2,
                                borderRadius: 1,
                                border: '2px solid #e0e0e0',
                            }}>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: '#0a6ed1', fontFamily: '"Arial", "Helvetica", sans-serif', fontSize: '15px' }}>
                                    {companies.find(c => c.CompanyID === Number(selectedCompanyId))?.CompanyName || 'Unknown'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#757575', fontFamily: '"Consolas", "Monaco", monospace', fontSize: '12px', mt: 0.5, display: 'block' }}>
                                    Code: {selectedCompanyCode || '—'} | Database: {selectedDatabaseName || '—'}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1 }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                color: '#f57c00',
                            }}>
                                <Box sx={{ height: 2, flexGrow: 1, width: 40, bgcolor: '#f57c00' }} />
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#f57c00', fontFamily: '"Arial", "Helvetica", sans-serif', fontSize: '13px' }}>
                                    CHANGING TO
                                </Typography>
                                <Box sx={{ height: 2, flexGrow: 1, width: 40, bgcolor: '#f57c00' }} />
                            </Box>
                        </Box>

                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: '#616161',
                                    mb: 1,
                                    fontFamily: '"Arial", "Helvetica", sans-serif',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                New Company
                            </Typography>
                            <Box sx={{
                                bgcolor: '#e3f2fd',
                                p: 2,
                                borderRadius: 1,
                                border: '2px solid #2196f3',
                            }}>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: '#1976d2', fontFamily: '"Arial", "Helvetica", sans-serif', fontSize: '15px' }}>
                                    {companies.find(c => String(c.CompanyID) === pendingCompanyId)?.CompanyName || 'Unknown'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#1976d2', fontFamily: '"Consolas", "Monaco", monospace', fontSize: '12px', mt: 0.5, display: 'block' }}>
                                    Code: {companies.find(c => String(c.CompanyID) === pendingCompanyId)?.CompanyCode || '—'} | Database: {companies.find(c => String(c.CompanyID) === pendingCompanyId)?.DatabaseName || '—'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ mt: 3, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc80' }}>
                        <Typography variant="body2" sx={{ color: '#e65100', fontFamily: '"Arial", "Helvetica", sans-serif', fontSize: '13px', fontWeight: 500 }}>
                            <strong>Important:</strong> This action will reload your user permissions and may affect your current work. All unsaved changes may be lost.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5, justifyContent: 'center' }}>
                    <Button
                        onClick={cancelCompanyChange}
                        variant="outlined"
                        sx={{
                            borderColor: '#bdbdbd',
                            color: '#616161',
                            backgroundColor: '#f5f5f5',
                            fontFamily: '"Arial", "Helvetica", sans-serif',
                            fontSize: '13px',
                            fontWeight: 600,
                            textTransform: 'none',
                            py: 1,
                            px: 3,
                            minWidth: 120,
                            borderRadius: 1.5,
                            '&:hover': {
                                borderColor: '#9e9e9e',
                                bgcolor: '#eeeeee',
                            }
                        }}
                    >
                        No, Cancel
                    </Button>
                    <Button
                        onClick={() => pendingCompanyId && confirmCompanyChange(pendingCompanyId)}
                        variant="contained"
                        sx={{
                            bgcolor: '#f57c00',
                            fontFamily: '"Arial", "Helvetica", sans-serif',
                            fontSize: '13px',
                            fontWeight: 700,
                            textTransform: 'none',
                            py: 1,
                            px: 3,
                            minWidth: 120,
                            borderRadius: 1.5,
                            '&:hover': {
                                bgcolor: '#ef6c00',
                            },
                        }}
                        autoFocus
                    >
                        Yes, Change
                    </Button>
                </DialogActions>
             </Dialog>

             <SearchResults
                open={showSearchResults}
                query={globalSearchQuery}
                onClose={() => {
                    setShowSearchResults(false);
                    setGlobalSearchQuery('');
                }}
             />

             {/* Mobile Search — full-screen field opened from the AppBar icon. */}
             <ResponsiveDialog
                open={mobileSearchOpen}
                onClose={() => setMobileSearchOpen(false)}
                fullWidth
                maxWidth="sm"
                aria-labelledby="mobile-search-title"
             >
                <DialogTitle id="mobile-search-title" sx={{ pb: 1 }}>Search</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        placeholder="Search across PRs, POs, vendors..."
                        inputProps={{ 'aria-label': 'Global search' }}
                        value={globalSearchQuery}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGlobalSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: <Search sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />,
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setGlobalSearchQuery('');
                            setMobileSearchOpen(false);
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
             </ResponsiveDialog>

             {/* Mobile More — surfaces company picker + lock + user + logout
                 controls that are hidden on small viewports. */}
             <ResponsiveDialog
                open={mobileMoreOpen}
                onClose={() => setMobileMoreOpen(false)}
                fullWidth
                maxWidth="sm"
                aria-labelledby="mobile-more-title"
             >
                <DialogTitle id="mobile-more-title">Account &amp; Company</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Signed in as
                            </Typography>
                            <Typography sx={{ fontSize: 15, fontWeight: 500 }}>{user?.fullname || '—'}</Typography>
                        </Box>

                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Current company
                            </Typography>
                            <Typography sx={{ fontSize: 15, fontWeight: 500 }}>
                                {selectedCompanyCode ? `${selectedCompanyCode} · ` : ''}{selectedCompanyName || 'Select a company'}
                                {selectedDatabaseName ? ` (${selectedDatabaseName})` : ''}
                            </Typography>
                        </Box>

                        <FormControl size="small" fullWidth>
                            <InputLabel id="mobile-company-select-label">Change company</InputLabel>
                            <Select
                                labelId="mobile-company-select-label"
                                id="mobile-company-select"
                                value={selectedCompanyId}
                                label="Change company"
                                onChange={(e) => handleCompanyChange(e.target.value as string)}
                                disabled={companyLocked}
                                renderValue={() => (selectedCompanyCode ? `${selectedCompanyCode} · ${selectedCompanyName}` : selectedCompanyName)}
                            >
                                {companies.map((c) => (
                                    <MenuItem key={c.CompanyID} value={String(c.CompanyID)} sx={{ whiteSpace: 'normal' }}>
                                        <Box>
                                            <Box sx={{ fontWeight: 500 }}>
                                                <Box component="span" sx={{ fontWeight: 600, color: '#0a6ed1', mr: 1 }}>{c.CompanyCode || '—'}</Box>
                                                {c.CompanyName}
                                            </Box>
                                            <Box sx={{ fontSize: 12, color: 'text.secondary', fontFamily: '"Consolas", "Monaco", monospace' }}>
                                                {c.DatabaseName || '—'}
                                            </Box>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Button
                            variant={companyLocked ? 'contained' : 'outlined'}
                            color={companyLocked ? 'error' : 'primary'}
                            startIcon={companyLocked ? <Lock /> : <LockOpen />}
                            onClick={() => setCompanyLocked((v) => !v)}
                            aria-pressed={!companyLocked}
                            fullWidth
                        >
                            {companyLocked ? 'Locked — tap to unlock' : 'Unlocked — tap to lock'}
                        </Button>

                        <Button
                            variant="outlined"
                            onClick={() => { setMobileMoreOpen(false); setSapAccountOpen(true); }}
                            fullWidth
                        >
                            My SAP B1 account
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<Logout />}
                            onClick={() => { setMobileMoreOpen(false); handleLogout(); }}
                            fullWidth
                        >
                            Logout
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMobileMoreOpen(false)}>Close</Button>
                </DialogActions>
             </ResponsiveDialog>
             <MySapAccountDialog open={sapAccountOpen} onClose={() => setSapAccountOpen(false)} />
         </Box>
     );
};

export default MainLayout;
